import DataTable from "@/components/DataTable/DataTable";
import type { ApiRequestParams, ApiResponse, DataTableColumn, DataTableFilter } from "@/components/DataTable/types";
import { ActionMenu } from "@/components/admin";
import { UserType, VendorType } from "@/interfaces";
import { useUserStore } from "@/stores/userStore";
import { formatCompanyDate } from "@/utils/dateUtils";
import { Badge, Table } from "antd";
import { useCallback } from "react";

interface Props {
  onView: (vendor: VendorType) => void;
  onEdit: (vendor: VendorType) => void;
  onToggleActive: (vendor: VendorType) => void;
  onEditUser: (vendor: VendorType, user: UserType) => void;
  fetchData: (params: ApiRequestParams) => Promise<ApiResponse<VendorType>>;
  onTableChange: (params: ApiRequestParams) => void;
  onExport: (type: "csv" | "excel") => void;
  refreshKey?: number;
  canEdit?: boolean;
  showCompanyColumn?: boolean;
}

export const VendorTable = ({
  onView,
  onEdit,
  onToggleActive,
  onEditUser,
  fetchData,
  onTableChange,
  onExport,
  refreshKey,
  canEdit = true,
  showCompanyColumn = false,
}: Props) => {
  const dateFormat = useUserStore((state) => state.currentCompany?.dateFormat);

  const getExpandedColumns = useCallback(
    (vendor: VendorType) => [
      {
        title: "User Name",
        dataIndex: "firstName",
        key: "name",
        render: (_: unknown, record: UserType) => `${record.firstName} ${record.lastName}`.trim() || "—",
      },
      {
        title: "Email",
        dataIndex: "email",
        key: "email",
        render: (value: unknown) => (value as string) || "—",
      },
      {
        title: "Phone",
        dataIndex: "phone",
        key: "phone",
        render: (value: unknown) => (value as string) || "—",
      },
      {
        title: "Primary",
        dataIndex: "isPrimary",
        key: "isPrimary",
        render: (value: unknown) => <Badge color={value ? "blue" : "default"} text={value ? "Yes" : "No"} />,
      },
      {
        title: "Status",
        dataIndex: "isActive",
        key: "isActive",
        render: (value: unknown) => <Badge color={value ? "green" : "red"} text={value ? "Active" : "Inactive"} />,
      },
      ...(canEdit
        ? [
            {
              title: "Actions",
              key: "actions",
              render: (_: unknown, record: UserType) => (
                <ActionMenu items={[{ key: "edit", label: "Edit User" }]} onSelect={() => onEditUser(vendor, record)} />
              ),
            },
          ]
        : []),
    ],
    [canEdit, onEditUser],
  );

  const columns: DataTableColumn<VendorType>[] = [
    {
      title: "Vendor Name",
      dataIndex: "vendorName",
      key: "vendorName",
      sortable: true,
      // Prefer this company's display name for the vendor (company_vendors), then the vendor name,
      // then the vendor's legal name.
      render: (value: unknown, record: VendorType) =>
        record.displayName || (value as string) || record.vendorLegalName || "—",
    },
    ...(showCompanyColumn
      ? [
          {
            title: "Company",
            dataIndex: "companies",
            key: "companies",
            render: (_: unknown, record: VendorType) => record.companies?.map((c) => c.companyName).join(", ") || "—",
          },
        ]
      : []),
    {
      title: "Users",
      dataIndex: "users",
      key: "users",
      render: (_: unknown, record: VendorType) => record.users?.length ?? 0,
    },
    {
      title: "Approval",
      dataIndex: "approvalStatus",
      key: "approvalStatus",
      render: (value: unknown) => {
        const status = value as string | undefined;
        const color = status === "Approved" ? "green" : status === "Rejected" ? "red" : "orange";
        return <Badge color={color} text={status || "—"} />;
      },
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      sortable: true,
      render: (value: unknown) => <Badge color={value ? "green" : "red"} text={value ? "Active" : "Inactive"} />,
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      sortable: true,
      render: (value: unknown) => formatCompanyDate(value as string, dateFormat),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: unknown, record: VendorType) => {
        const items = [
          { key: "view", label: "View Vendor Details" },
          ...(canEdit
            ? [
                { key: "edit", label: "Edit Vendor" },
                record.isActive
                  ? { key: "deactivate", label: "Deactivate" }
                  : { key: "activate", label: "Activate" },
              ]
            : []),
        ];
        return (
          <ActionMenu
            items={items}
            onSelect={(key) => {
              if (key === "view") onView(record);
              if (key === "edit") onEdit(record);
              if (key === "activate" || key === "deactivate") onToggleActive(record);
            }}
          />
        );
      },
    },
  ];

  const filters: DataTableFilter[] = [
    {
      label: "Status",
      key: "isActive",
      type: "select",
      placeholder: "All",
      options: [
        { label: "Active", value: "true" },
        { label: "Inactive", value: "false" },
      ],
    },
  ];

  return (
    <DataTable<VendorType>
      columns={columns}
      fetchData={fetchData}
      searchable
      searchPlaceholder="Search vendors..."
      filters={filters}
      actionColumnTitle="Actions"
      exportOptions={{
        csv: true,
        excel: true,
        filename: "vendors-export",
        onExport,
      }}
      pagination
      defaultPageSize={10}
      rowKey="id"
      refreshKey={refreshKey}
      onChange={onTableChange}
      tableProps={{
        expandable: {
          expandedRowRender: (record: VendorType) => (
            <Table<UserType>
              columns={getExpandedColumns(record)}
              dataSource={record.users || []}
              rowKey="id"
              pagination={false}
              size="small"
            />
          ),
          rowExpandable: (record: VendorType) => (record.users?.length || 0) > 0,
        },
      }}
    />
  );
};
